import { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/Placementdb.css';
import '../styles/FilterStyles.css';
import '../styles/FilterStyles.css';
import '../styles/Advanced.css';
import ProfileAvatarpp from '../components/common/Avatar/ProfileAvatarpp';
import { useNavigate } from 'react-router-dom';

// Components
import ColumnSelectionModal from '../components/Staffrecord/ColumnSelectionModal';
import DataTable from '../components/Placementdb/DataTable';
import Pagination from '../components/Placementdb/Pagination';
import LoadingErrorState from '../components/Staffrecord/LoadingErrorState';
import { useToast } from '../components/Admissionform/ToastManager';
import UserProfileDropdown from '../components/common/Avatar/UserProfileDropdown';

// New Components
import SearchBar from '../components/Placementdb/SearchBar';
import FilterSummary from '../components/Placementdb/FilterSummary';
import FiltersPanel from '../components/Placementdb/FiltersPanel';
import RecordCountCard from '../components/Placementdb/RecordCountCard';

// Hooks and Utils
import { useFilterPersistence } from '../utils/useFilterPersistence';
import { useStudentData, useColumnManagement } from '../utils/hookspdb';
import { downloadFilteredResumes, downloadFilteredExcel } from '../utils/excelUtilspb';
import { PERMANENT_COLUMNS, ALL_COLUMNS } from '../utils/tableConstants';

const Placementdb = ({ type = "placementEligible", pageKey = "default" }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  // Initial filter structure
  const initialFilters = {
    selectedDepartments: [],
    selectedSkillsKnown: [],
    selectedBranches: [],
    selectedCompanyLocations: [],
    selectedExperiences: [],
    selectedCourses: [],
    selectedGenders: [],
    fromPassedYear: '',
    toPassedYear: '',
    selectedResumeStatus: '',
    readyForPlacement: '',
    searchField: 'studentName',
    searchValue: '',
    currentPage: 1,
    recordsPerPage: 10
  };
  const storageKey = `placementDbFilters_${type}_${pageKey}`;
  // Use the persistence hook
  const { filters, updateFilters, resetFilters, isReady } = useFilterPersistence(initialFilters, storageKey);

  // Pending filters for "Apply" functionality
  const [pendingDepartments, setPendingDepartments] = useState([]);
  const [pendingSkillsKnown, setPendingSkillsKnown] = useState([]);
  const [pendingBranches, setPendingBranches] = useState([]);
  const [pendingCompanyLocations, setPendingCompanyLocations] = useState([]);
  const [pendingExperiences, setPendingExperiences] = useState([]);
  const [pendingCourses, setPendingCourses] = useState([]);
  const [pendingFromPassedYear, setPendingFromPassedYear] = useState('');
  const [pendingToPassedYear, setPendingToPassedYear] = useState('');
  const [pendingResumeStatus, setPendingResumeStatus] = useState('');
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [pendingGenders, setPendingGenders] = useState([]);


  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [pendingReadyForPlacement, setPendingReadyForPlacement] = useState('');

  // Data states
  const [userBranches, setUserBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [skillsKnown, setSkillsKnown] = useState([]);
  const [companyLocations, setCompanyLocations] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [courses, setCourses] = useState([]);
  const lastFetchParamsRef = useRef('');

  // Initialize pending filters when persisted filters are loaded
  useEffect(() => {
    if (isReady) {
      console.log('Initializing pending filters with:', filters);
      setPendingDepartments(filters.selectedDepartments || []);
      setPendingSkillsKnown(filters.selectedSkillsKnown || []);
      setPendingBranches(filters.selectedBranches || []);
      setPendingCompanyLocations(filters.selectedCompanyLocations || []);
      setPendingExperiences(filters.selectedExperiences || []);
      setPendingCourses(filters.selectedCourses || []);
      setPendingFromPassedYear(filters.fromPassedYear || '');
      setPendingToPassedYear(filters.toPassedYear || '');
      setPendingResumeStatus(filters.selectedResumeStatus || '');
      setPendingGenders(filters.selectedGenders || []);
      setPendingReadyForPlacement(filters.readyForPlacement || '');

    }
  }, [isReady, filters]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(filters.searchValue || '');
    }, 1000);
    return () => clearTimeout(timer);
  }, [filters.searchValue]);

  // Custom hooks
  const {
    data,
    loading,
    error,
    pagination,
    fetchStudentRecords,
    updateStudentRecord,
    changePage,
    changeItemsPerPage
  } = useStudentData();

  const {
    selectedColumns,
    tempSelectedColumns,
    selectAll,
    isModalOpen,
    toggleSelectAll,
    toggleColumnSelection,
    confirmColumnSelection,
    cancelColumnSelection,
    openColumnModal,
    closeColumnModal
  } = useColumnManagement(PERMANENT_COLUMNS, ALL_COLUMNS);

  const searchableColumns = ALL_COLUMNS.filter(column =>
    selectedColumns.includes(column.key)
  );

  // Fetch departments data
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/alltech`, {
          method: 'GET',
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` }),
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data && Array.isArray(result.data)) {
            const uniqueDepartments = [...new Set(result.data.map(item => item.Department))].filter(Boolean);
            setDepartments(uniqueDepartments);
          }
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
        showError('Error loading departments.');
      }
    };

    fetchDepartments();
  }, [showError]);

  // Update available skills when pending departments change
  useEffect(() => {
    const fetchCoursesByDepartment = async () => {
      if (pendingDepartments.length > 0 && pendingDepartments[0]) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/course-by-department?Department=${encodeURIComponent(pendingDepartments[0])}`,
            {
              method: 'GET',
              headers: {
                ...(token && { 'Authorization': `Bearer ${token}` }),
                'Content-Type': 'application/json'
              }
            }
          );

          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              setSkillsKnown(result.skills || []);
              setCourses(result.courses || []);
            }
          }
        } catch (error) {
          console.error('Error fetching courses and skills:', error);
          showError('Error loading courses and skills.');
        }
      } else {
        setSkillsKnown([]);
        setCourses([]);
        setPendingSkillsKnown([]);
        setPendingCourses([]);
      }
    };

    fetchCoursesByDepartment();
  }, [pendingDepartments, showError]);

  // Fetch static data
  useEffect(() => {
    const locationData = [
      'Bangalore', 'Chennai', 'Hosur', 'Trichy', 'Madurai', 'Hyderabad', 'Pune', 'Mumbai', 'Gurgaon',
      'Noida', 'Kolkata', 'Ahmedabad', 'Trivandrum', 'Coimbatore',
      'Mohali', 'Indore', 'Jaipur', 'Nagpur',
    ];

    const experienceData = [
      'Fresher', '<1year', '1-2years',
      '2-3years', '>3years'
    ];

    setCompanyLocations(locationData);
    setExperiences(experienceData);
  }, []);

  // Fetch all branches
  useEffect(() => {
    const fetchAllBranches = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/location`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (response.ok) {
          const locationData = await response.json();
          if (Array.isArray(locationData)) {
            const uniqueBranches = [...new Set(
              locationData
                .filter(item => item.status === true)
                .map(item => item.branch)
            )];
            setUserBranches(uniqueBranches);
          }
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
        showError('Error loading branches.');
      }
    };

    fetchAllBranches();
  }, [showError]);

  // Memoize filter dependencies for main data fetching
  const departmentsString = filters.selectedDepartments?.join(',') || '';
  const skillsString = filters.selectedSkillsKnown?.join(',') || '';
  const coursesString = filters.selectedCourses?.join(',') || '';
  const gendersString = filters.selectedGenders?.join(',') || '';
  const branchesString = filters.selectedBranches?.join(',') || '';
  const locationsString = filters.selectedCompanyLocations?.join(',') || '';
  const experiencesString = filters.selectedExperiences?.join(',') || '';

  // Main data fetching useEffect
  useEffect(() => {
    if (!isReady) return;

    const currentParams = JSON.stringify({
      page: filters.currentPage,
      limit: filters.recordsPerPage,
      search: debouncedSearchValue,
      dept: filters.selectedDepartments,
      skills: filters.selectedSkillsKnown,
      courses: filters.selectedCourses,
      genders: filters.selectedGenders,        // ✅ ADDED
      branches: filters.selectedBranches,
      locations: filters.selectedCompanyLocations,
      experiences: filters.selectedExperiences,
      resume: filters.selectedResumeStatus,
      fromYear: filters.fromPassedYear,
      toYear: filters.toPassedYear,
      ready: filters.readyForPlacement
    });

    if (currentParams === lastFetchParamsRef.current) {
      console.log('Skipping fetch - params unchanged');
      return;
    }

    let isCancelled = false;

    const fetchData = async () => {
      const searchParams = {
        page: filters.currentPage || 1,
        limit: filters.recordsPerPage || 10,
      };

      if (debouncedSearchValue) {
        searchParams.searchField = filters.searchField;
        searchParams.searchValue = debouncedSearchValue;
      }

      if (filters.selectedDepartments?.length > 0) {
        searchParams.departments = filters.selectedDepartments.join(',');
      }
      if (filters.selectedSkillsKnown?.length > 0) {
        searchParams.skillsKnown = filters.selectedSkillsKnown.join(',');
      }
      if (filters.selectedCourses?.length > 0) {
        searchParams.courses = filters.selectedCourses.join(',');
      }
      if (filters.selectedBranches?.length > 0) {
        searchParams.branches = filters.selectedBranches.join(',');
      }
      if (filters.selectedCompanyLocations?.length > 0) {
        searchParams.companyLocations = filters.selectedCompanyLocations.join(',');
      }
      if (filters.selectedExperiences?.length > 0) {
        searchParams.experiences = filters.selectedExperiences.join(',');
      }
      if (filters.selectedResumeStatus) {
        searchParams.resumeStatus = filters.selectedResumeStatus;
      }
      if (filters.fromPassedYear) {
        searchParams.fromPassedYear = filters.fromPassedYear;
      }
      if (filters.toPassedYear) {
        searchParams.toPassedYear = filters.toPassedYear;
      }
      if (filters.selectedGenders?.length > 0) {
        searchParams.genders = filters.selectedGenders.join(',');
      }
      if (filters.readyForPlacement?.trim()) {
        searchParams.readyForPlacement = filters.readyForPlacement;
      }

      if (!isCancelled) {
        console.log('Fetching with filters:', searchParams);
        lastFetchParamsRef.current = currentParams;
        await fetchStudentRecords(searchParams, type);
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [
    isReady,
    debouncedSearchValue,
    filters.searchField,
    filters.currentPage,
    filters.recordsPerPage,
    departmentsString,
    skillsString,
    coursesString,
    gendersString,          // ✅ ADDED
    branchesString,
    locationsString,
    experiencesString,
    filters.selectedResumeStatus,
    filters.fromPassedYear,
    filters.toPassedYear,
    filters.readyForPlacement,
    fetchStudentRecords
  ]);

  // Apply filters
  const handleApplyFilters = async () => {
    setIsApplyingFilters(true);

    try {
      updateFilters({
        selectedDepartments: [...pendingDepartments],
        selectedSkillsKnown: [...pendingSkillsKnown],
        selectedBranches: [...pendingBranches],
        selectedCompanyLocations: [...pendingCompanyLocations],
        selectedExperiences: [...pendingExperiences],
        selectedCourses: [...pendingCourses],
        selectedGenders: [...pendingGenders],
        selectedResumeStatus: pendingResumeStatus,
        fromPassedYear: pendingFromPassedYear,
        toPassedYear: pendingToPassedYear,
        readyForPlacement: pendingReadyForPlacement,
        currentPage: 1
      });

      showSuccess('Filters applied successfully!');
    } catch (error) {
      console.error('Error applying filters:', error);
      showError('Failed to apply filters. Please try again.');
    } finally {
      setIsApplyingFilters(false);
    }
  };

  // Check for pending changes
  const hasPendingChanges = () => {
    return (
      JSON.stringify(pendingDepartments.sort()) !== JSON.stringify((filters.selectedDepartments || []).sort()) ||
      JSON.stringify(pendingSkillsKnown.sort()) !== JSON.stringify((filters.selectedSkillsKnown || []).sort()) ||
      JSON.stringify(pendingBranches.sort()) !== JSON.stringify((filters.selectedBranches || []).sort()) ||
      JSON.stringify(pendingCompanyLocations.sort()) !== JSON.stringify((filters.selectedCompanyLocations || []).sort()) ||
      JSON.stringify(pendingExperiences.sort()) !== JSON.stringify((filters.selectedExperiences || []).sort()) ||
      JSON.stringify(pendingCourses.sort()) !== JSON.stringify((filters.selectedCourses || []).sort()) ||
      JSON.stringify(pendingGenders.sort()) !== JSON.stringify((filters.selectedGenders || []).sort()) ||
      pendingResumeStatus !== (filters.selectedResumeStatus || '') ||
      pendingFromPassedYear !== (filters.fromPassedYear || '') ||
      pendingToPassedYear !== (filters.toPassedYear || '') ||
      pendingReadyForPlacement !== (filters.readyForPlacement || '')
    );
  };

  // Clear all filters
  const clearAllFilters = () => {
    setPendingDepartments([]);
    setPendingSkillsKnown([]);
    setPendingBranches([]);
    setPendingCompanyLocations([]);
    setPendingExperiences([]);
    setPendingCourses([]);
    setPendingGenders([]);
    setPendingResumeStatus('');
    setPendingFromPassedYear('');
    setPendingToPassedYear('');
    setPendingReadyForPlacement('');
    resetFilters();
  };

  // Build current filters utility function
  const buildCurrentFilters = useCallback(() => {
    const currentFilters = {
      searchField: filters.searchValue ? filters.searchField : undefined,
      searchValue: filters.searchValue || undefined,
      departments: filters.selectedDepartments?.length > 0 ? filters.selectedDepartments.join(',') : undefined,
      skillsKnown: filters.selectedSkillsKnown?.length > 0 ? filters.selectedSkillsKnown.join(',') : undefined,
      courses: filters.selectedCourses?.length > 0 ? filters.selectedCourses.join(',') : undefined,
      genders: filters.selectedGenders?.length > 0 ? filters.selectedGenders.join(',') : undefined,
      branches: filters.selectedBranches?.length > 0 ? filters.selectedBranches.join(',') : undefined,
      companyLocations: filters.selectedCompanyLocations?.length > 0 ? filters.selectedCompanyLocations.join(',') : undefined,
      experiences: filters.selectedExperiences?.length > 0 ? filters.selectedExperiences.join(',') : undefined,
      fromPassedYear: filters.fromPassedYear || undefined,
      toPassedYear: filters.toPassedYear || undefined,
      resumeStatus: filters.selectedResumeStatus || undefined,
      readyForPlacement: filters.readyForPlacement || undefined

    };

    Object.keys(currentFilters).forEach(key =>
      currentFilters[key] === undefined && delete currentFilters[key]
    );

    return currentFilters;
  }, [filters]);

  // Page change handler
  const handlePageChange = async (page) => {
    if (page >= 1 && page <= pagination.totalPages && page !== pagination.currentPage) {
      const currentFilters = buildCurrentFilters();
      await changePage(page, currentFilters, type);
      updateFilters({ currentPage: page });
    }
  };

  const handleItemsPerPageChange = async (newLimit) => {
    const currentFilters = buildCurrentFilters();
    await changeItemsPerPage(newLimit, currentFilters, type);
    updateFilters({ recordsPerPage: newLimit, currentPage: 1 });
  };

  const handleDownloadExcel = async () => {
    const currentFilters = buildCurrentFilters();
    await downloadFilteredExcel(currentFilters, type, showSuccess, showError);
  };

  const handleStudentSelection = (studentId, isSelected) => {
    const newSelectedStudents = new Set(selectedStudents);
    if (isSelected) {
      newSelectedStudents.add(studentId);
    } else {
      newSelectedStudents.delete(studentId);
    }
    setSelectedStudents(newSelectedStudents);
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const currentPageStudentIds = data.map(student => student.studentId);
      const newSelectedStudents = new Set([...selectedStudents, ...currentPageStudentIds]);
      setSelectedStudents(newSelectedStudents);
    } else {
      const currentPageStudentIds = new Set(data.map(student => student.studentId));
      const newSelectedStudents = new Set([...selectedStudents].filter(id => !currentPageStudentIds.has(id)));
      setSelectedStudents(newSelectedStudents);
    }
  };

  const handleBulkDownload = async () => {
    if (selectedStudents.size === 0) {
      showError('Please select at least one student to download resumes.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/resumes/download-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          studentIds: Array.from(selectedStudents)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download resumes');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const currentDate = new Date().toISOString().split('T')[0];
      link.download = `Selected_Resumes_${currentDate}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccess(`Successfully downloaded ${selectedStudents.size} resume(s)!`);
      setSelectedStudents(new Set());
    } catch (error) {
      console.error('Error downloading bulk resumes:', error);
      showError(`Failed to download resumes: ${error.message}`);
    }
  };

  const handleDownload = async () => {
    const currentFilters = buildCurrentFilters();
    await downloadFilteredResumes(currentFilters, type, showSuccess, showError);
  };

  const handleEdit = (rowIndex) => {
    const rowData = { ...data[rowIndex] };
    console.log('Navigating to edit with current filters:', filters);
    navigate(`/edit-student/${rowData.studentId}`);
  };

  // Note: Removed unused handleSave function

  const handleStaffUpdate = async (displayRowIndex, staffData) => {
    try {
      if (!data[displayRowIndex]) {
        throw new Error('Invalid record index');
      }

      const currentRecord = { ...data[displayRowIndex] };
      const updatedRecord = {
        ...currentRecord,
        staffId2: staffData.staffId2 || '',
        staffName2: staffData.staffName2 || '',
        staffId3: staffData.staffId3 || '',
        staffName3: staffData.staffName3 || ''
      };

      await updateStudentRecord(updatedRecord);
      const currentFilters = buildCurrentFilters();
      await fetchStudentRecords({
        page: filters.currentPage || 1,
        limit: filters.recordsPerPage || 10,
        ...currentFilters
      }, type);

      showSuccess('Staff details updated successfully!');
    } catch (error) {
      console.error('Staff update error:', error);
      showError('Failed to update staff details. Please try again.');
    }
  };

  const loadingErrorComponent = LoadingErrorState({
    loading,
    error,
    onRetry: () => fetchStudentRecords({
      page: filters.currentPage || 1,
      limit: filters.recordsPerPage || 10
    }, type)
  });

  if (loadingErrorComponent) {
    return loadingErrorComponent;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-heading">
          {type === "placementEligible" && "STUDENTS - AWAITING PLACEMENT"}
          {type === "getAll" && "STUDENTS - AWAITING PLACEMENT"}
          {type === "mentor" && "    MENTOR DASHBOARD"}
        </h1>        <div className="flex items-center gap-4">
          <UserProfileDropdown />
          <ProfileAvatarpp />
        </div>
      </div>

      <RecordCountCard
        currentCount={pagination.recordsOnCurrentPage || data.length}
        totalCount={pagination.totalRecords}
      />

      <div className='head'>
        <h3>Search Student Records</h3>
      </div>

      <FilterSummary
        filters={filters}
        updateFilters={updateFilters}
        setPendingDepartments={setPendingDepartments}
        setPendingSkillsKnown={setPendingSkillsKnown}
        setPendingCourses={setPendingCourses}
        setPendingBranches={setPendingBranches}
        setPendingCompanyLocations={setPendingCompanyLocations}
        setPendingExperiences={setPendingExperiences}
        setPendingResumeStatus={setPendingResumeStatus}
        setPendingFromPassedYear={setPendingFromPassedYear}
        setPendingToPassedYear={setPendingToPassedYear}
        setPendingReadyForPlacement={setPendingReadyForPlacement}
        clearAllFilters={clearAllFilters}
      />

      <FiltersPanel
        // Data
        userBranches={userBranches}
        departments={departments}
        skillsKnown={skillsKnown}
        companyLocations={companyLocations}
        experiences={experiences}
        courses={courses}

        // Pending states
        pendingBranches={pendingBranches}
        pendingDepartments={pendingDepartments}
        pendingSkillsKnown={pendingSkillsKnown}
        pendingCourses={pendingCourses}
        pendingCompanyLocations={pendingCompanyLocations}
        pendingExperiences={pendingExperiences}
        pendingResumeStatus={pendingResumeStatus}
        pendingFromPassedYear={pendingFromPassedYear}
        pendingToPassedYear={pendingToPassedYear}
        pendingGenders={pendingGenders}
        pendingReadyForPlacement={pendingReadyForPlacement}

        // Setters
        setPendingGenders={setPendingGenders}
        setPendingBranches={setPendingBranches}
        setPendingDepartments={setPendingDepartments}
        setPendingSkillsKnown={setPendingSkillsKnown}
        setPendingCourses={setPendingCourses}
        setPendingCompanyLocations={setPendingCompanyLocations}
        setPendingExperiences={setPendingExperiences}
        setPendingResumeStatus={setPendingResumeStatus}
        setPendingFromPassedYear={setPendingFromPassedYear}
        setPendingToPassedYear={setPendingToPassedYear}
        setPendingReadyForPlacement={setPendingReadyForPlacement}


        // Actions
        clearAllFilters={clearAllFilters}
        handleApplyFilters={handleApplyFilters}
        isApplyingFilters={isApplyingFilters}
        hasPendingChanges={hasPendingChanges}
        type={type}
      />

      <SearchBar
        searchField={filters.searchField}
        search={filters.searchValue}
        searchableColumns={searchableColumns}
        onFieldChange={(value) => updateFilters({ searchField: value })}
        onSearchChange={(value) => updateFilters({ searchValue: value })}
      />

      <>
        <DataTable
          currentItems={data}
          selectedColumns={selectedColumns}
          indexOfFirstItem={0}
          onEdit={handleEdit}
          onStaffUpdate={handleStaffUpdate}
          selectedStudents={selectedStudents}
          onStudentSelection={handleStudentSelection}
          onSelectAll={handleSelectAll}
          onBulkDownload={handleBulkDownload}
          type={type}
        />

        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          itemsPerPage={pagination.recordsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onOpenColumnModal={openColumnModal}
          onDownload={handleDownload}
          onDownloadExcel={handleDownloadExcel}

        />
      </>

      <ColumnSelectionModal
        isOpen={isModalOpen}
        tempSelectedColumns={tempSelectedColumns}
        selectAll={selectAll}
        onToggleSelectAll={toggleSelectAll}
        onToggleColumn={toggleColumnSelection}
        onConfirm={confirmColumnSelection}
        onCancel={cancelColumnSelection}
        onClose={closeColumnModal}
      />
    </div>
  );
};

export default Placementdb;